import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Link } from '~/lib/react-router-dom';

import EditIcon from '@mui/icons-material/Edit';
import { IconButton, Stack, Typography } from '@mui/material';

import PermissionedComponent from '~/components/Common/PermissionedComponent';
import Spinner from '~/components/Common/Spinner';

import { ROLE_ADMIN_USER } from '~/consts/roles';

import { getUser, updateUser } from '~/services/AgentHub/AgentService';

import MoveUserAgencyModal from './MoveUserAgencyModal';
import EditTierForm from './Tier/EditTierForm';

interface Props {
  id: string;
}

type Tier = 'tier1' | 'tier2' | 'tier3';

export default function UserAgencyDetails({ id }: Props) {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(undefined);
  const [showEditTierForm, setShowEditTierForm] = useState(false);
  const [showMoveAgencyModal, setShowMoveAgencyModal] = useState(false);
  const [tier, setTier] = useState<Tier>('tier1');
  const { enqueueSnackbar } = useSnackbar();

  const fetchUserData = useCallback(
    async (id: string) => {
      try {
        setLoading(true);

        const user = await getUser(id);

        setUser(user);
        setTier(user.tier);
      } catch (err) {
        enqueueSnackbar('Failed to load data', {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    fetchUserData(id);
  }, [fetchUserData, id]);

  async function onUpdateTier() {
    const updatedUser = await updateUser(user.id, { tier });
    setUser(updatedUser);
  }

  const urlBuilder = () => {
    return `/users-list/agencies?id=${user.agencyId}`;
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      {user && (
        <Stack>
          <Typography>Agency Name:</Typography>
          <Stack direction="row" alignItems="center">
            <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER]}>
              <IconButton aria-label="Edit agency" onClick={() => setShowMoveAgencyModal(true)}>
                <EditIcon sx={{ fontSize: 16 }} color="primary" />
              </IconButton>
            </PermissionedComponent>
            <Typography fontWeight="bold">
              <Link target="_blank" rel="noreferrer" to={urlBuilder()}>
                {user.agencyName}
              </Link>
            </Typography>
          </Stack>
          <Typography mt={2}>Affiliation:</Typography>
          <Typography fontWeight="bold">{user.affiliation}</Typography>

          <Typography mt={2}>Agent Tier:</Typography>
          <Stack direction="row" alignItems="center">
            {!showEditTierForm && (
              <>
                <IconButton onClick={() => setShowEditTierForm(true)}>
                  <EditIcon sx={{ fontSize: 16 }} color="primary" />
                </IconButton>
                <Typography fontWeight="bold">{user.tier}</Typography>
              </>
            )}

            {showEditTierForm && (
              <EditTierForm
                onSubmit={onUpdateTier}
                setShowEditTierForm={setShowEditTierForm}
                setTier={setTier}
                tier={tier}
              />
            )}
          </Stack>
          {showMoveAgencyModal && (
            <MoveUserAgencyModal
              isOpen={showMoveAgencyModal}
              onClose={() => setShowMoveAgencyModal(false)}
              onMoved={setUser}
              userId={user.id}
              currentAgencyId={user.agencyId}
            />
          )}
        </Stack>
      )}
    </>
  );
}
